import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { VAT_TREATMENTS, type EbLedgerOption, type EbRelationOption, type PurchaseInvoiceDetail as Detail } from '@aviaconsole/shared';
import { capi, companyUrl, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { formatDate, formatEur } from '../lib/format';
import {
  BackLink, Button, Card, Chip, ConfirmDialog, Input, Loading, PageHeader, Select, purchaseInvoiceStatusLabel, purchaseInvoiceTone, vatTreatmentLabel,
} from '../components/ui';
import { PdfFrame } from '../components/PdfPreview';

const money = (c: number) => (c / 100).toFixed(2).replace('.', ',');
const parseMoney = (v: string) => Math.round(Number(v.replace(/\./g, '').replace(',', '.')) * 100) || 0;

interface Form {
  ebRelationId: string; number: string; date: string; description: string;
  vatTreatment: Detail['vatTreatment']; subtotal: string; vatAmount: string; total: string;
}

export function PurchaseInvoiceDetail() {
  const { id } = useParams();
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const key = ['purchase-invoice', company?.id, id];

  const q = useQuery({ queryKey: key, queryFn: () => capi<Detail>(`/purchase-invoices/${id}`) });
  const relations = useQuery({ queryKey: ['eb-relations', company?.id], queryFn: () => capi<EbRelationOption[]>('/purchase-invoices/relations') });
  const ledgers = useQuery({ queryKey: ['purchase-ledgers', company?.id], queryFn: () => capi<EbLedgerOption[]>('/purchase-invoices/ledgers') });

  const row = q.data;
  const [f, setF] = useState<Form | null>(null);
  const [ledgerId, setLedgerId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setF({
      ebRelationId: row.ebRelationId ? String(row.ebRelationId) : '', number: row.number ?? '', date: row.date ?? '',
      description: row.description ?? '', vatTreatment: row.vatTreatment, subtotal: money(row.subtotalCents), vatAmount: money(row.vatCents), total: money(row.totalCents),
    });
  }, [row]);
  useEffect(() => { if (ledgers.data?.length && !ledgerId) setLedgerId(String(row?.ebLedgerId ?? ledgers.data[0]!.id)); }, [ledgers.data, row?.ebLedgerId, ledgerId]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => (p ? { ...p, [k]: v } : p));
  const locked = row?.status === 'booked';

  const save = useMutation({
    mutationFn: () => capi<Detail>(`/purchase-invoices/${id}`, {
      method: 'PATCH',
      json: {
        ebRelationId: f!.ebRelationId ? Number(f!.ebRelationId) : null, number: f!.number || null, date: f!.date || null,
        description: f!.description || null, vatTreatment: f!.vatTreatment, subtotalCents: parseMoney(f!.subtotal),
        vatCents: parseMoney(f!.vatAmount), totalCents: parseMoney(f!.total),
      },
    }),
    onSuccess: (data) => { qc.setQueryData(key, data); qc.invalidateQueries({ queryKey: ['purchase-invoices'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const setStatus = useMutation({
    mutationFn: (status: 'pending' | 'ignored') => capi<Detail>(`/purchase-invoices/${id}`, { method: 'PATCH', json: { status } }),
    onSuccess: (data) => { qc.setQueryData(key, data); qc.invalidateQueries({ queryKey: ['purchase-invoices'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const del = useMutation({
    mutationFn: () => capi(`/purchase-invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['purchase-invoices'] }); navigate('/dossier'); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const book = useMutation({
    mutationFn: (force: boolean) => capi<Detail>(`/purchase-invoices/${id}/book`, { method: 'POST', json: { ledgerId: Number(ledgerId), vatTreatment: f?.vatTreatment, force } }),
    onSuccess: (data) => { qc.setQueryData(key, data); qc.invalidateQueries({ queryKey: ['purchase-invoices'] }); setDuplicateNotice(null); toast.push(t('done')); },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.status === 409) { setDuplicateNotice(e.message); return; }
      toast.push(e.message, 'error');
    },
  });

  if (!row || !f) return <Loading />;
  const relationOptions = [{ value: '', label: '—' }, ...(relations.data ?? []).map((r) => ({ value: String(r.id), label: r.code ? `${r.name} (${r.code})` : r.name }))];
  const ledgerOptions = (ledgers.data ?? []).map((l) => ({ value: String(l.id), label: `${l.code} — ${l.description}` }));
  const canBook = !locked && !!f.ebRelationId && !!f.number && !!f.date;

  return (
    <>
      <PageHeader crumb={t('dossierDetailCrumb')} title={row.relationName || row.number || s('Unrecognised')} sub={row.date ? formatDate(row.date, lang) : undefined}
        actions={<>
          {row.status === 'duplicate' && <Button size="sm" icon="fas fa-check" onClick={() => setStatus.mutate('pending')}>{t('dossierNotDuplicate')}</Button>}
          {!locked && row.status !== 'ignored' && <Button size="sm" icon="fas fa-eye-slash" onClick={() => setStatus.mutate('ignored')}>{t('dossierIgnore')}</Button>}
          {!locked && row.status === 'ignored' && <Button size="sm" icon="fas fa-rotate-left" onClick={() => setStatus.mutate('pending')}>{t('dossierRestore')}</Button>}
          {!locked && <Button variant="danger" size="sm" icon="fas fa-trash-can" onClick={() => setConfirmDelete(true)}>{t('deleteFromDossier')}</Button>}
          <Chip tone={purchaseInvoiceTone(row.status)}>{s(purchaseInvoiceStatusLabel(row.status))}</Chip>
        </>} />

      {confirmDelete && (
        <ConfirmDialog title={t('confirmDeleteDossierTitle')} confirmLabel={t('deleteFromDossier')} cancelLabel={t('cancel')} danger pending={del.isPending} onConfirm={() => del.mutate()} onClose={() => setConfirmDelete(false)}>
          {t('confirmDeleteDossierBody')}
        </ConfirmDialog>
      )}

      <div className="grid two" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
        <div className="stack">
          <Card>
            <div className="row" style={{ marginBottom: '0.9rem' }}>
              <BackLink to="/dossier" label={t('backToDossier')} />
              <div className="spacer" />
              <Button size="sm" icon="fas fa-arrow-up-right-from-square" onClick={() => window.open(companyUrl(`/purchase-invoices/${id}/pdf`), '_blank')}>{t('openInNewTab')}</Button>
            </div>

            {row.status === 'duplicate' && row.duplicateNote && (
              <div className="banner tint-warning" style={{ marginBottom: '0.9rem' }}>
                <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                <span>{t('dossierDuplicateBanner', { note: row.duplicateNote })}</span>
              </div>
            )}

            <div className="grid fields" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
              <div style={{ gridColumn: 'span 2' }}><Select label={t('colSupplier')} value={f.ebRelationId} disabled={locked} options={relationOptions} onChange={(e) => set('ebRelationId', e.target.value)} /></div>
              <Input label={t('invoiceNumber')} value={f.number} disabled={locked} onChange={(e) => set('number', e.target.value)} />
              <Input label={t('colIssued')} type="date" value={f.date} disabled={locked} onChange={(e) => set('date', e.target.value)} />
              <Select label={t('vatTreatment')} value={f.vatTreatment} disabled={locked} options={VAT_TREATMENTS.map((v) => ({ value: v, label: s(vatTreatmentLabel(v)) }))} onChange={(e) => set('vatTreatment', e.target.value as Form['vatTreatment'])} />
              <Input label={t('amountExclVat')} inputMode="decimal" value={f.subtotal} disabled={locked} onChange={(e) => set('subtotal', e.target.value)} />
              <Input label={t('vat')} inputMode="decimal" value={f.vatAmount} disabled={locked} onChange={(e) => set('vatAmount', e.target.value)} />
              <Input label={t('totalDue')} inputMode="decimal" value={f.total} disabled={locked} onChange={(e) => set('total', e.target.value)} />
              <div style={{ gridColumn: 'span 2' }}><Input label={t('description')} value={f.description} disabled={locked} onChange={(e) => set('description', e.target.value)} /></div>
            </div>
            {!locked && <div className="row" style={{ marginTop: '1rem' }}><div className="spacer" /><Button variant="primary" icon="fas fa-floppy-disk" onClick={() => save.mutate()} disabled={save.isPending}>{t('save')}</Button></div>}
          </Card>

          {locked ? (
            <Card tint="success">
              <h2>{t('hEb')}</h2>
              <p style={{ margin: 0 }}>{t('dossierBooked', { entry: row.ebEntryNumber ?? String(row.ebMutationId) })}</p>
            </Card>
          ) : (
            <Card>
              <h2>{t('dossierBookHeading')}</h2>
              <p className="secondary" style={{ margin: '0 0 0.8rem', fontSize: '0.82rem' }}>{t('dossierBookHint')}</p>
              {!canBook && <p className="small" style={{ color: 'var(--warning)' }}>{t('dossierPickSupplierFirst')}</p>}
              {duplicateNotice && (
                <div className="banner tint-warning" style={{ marginBottom: '0.8rem' }}>
                  <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                  <span>{duplicateNotice}</span>
                </div>
              )}
              <div className="stack md">
                <Select label={t('dossierLedger')} value={ledgerId} options={ledgerOptions} onChange={(e) => setLedgerId(e.target.value)} />
                <div className="row" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                  {duplicateNotice && <Button variant="danger" icon="fas fa-triangle-exclamation" onClick={() => book.mutate(true)} disabled={book.isPending}>{t('dossierBookForce')}</Button>}
                  <Button variant="primary" icon="fas fa-book" onClick={() => book.mutate(false)} disabled={book.isPending || !canBook || !ledgerId}>{t('dossierBook')}</Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        <div style={{ position: 'sticky', top: '1rem', alignSelf: 'start' }}>
          <Card>
            <div className="row" style={{ marginBottom: '0.6rem' }}><h2 style={{ margin: 0 }}>{row.filename}</h2><div className="spacer" /><span className="small muted">{formatEur(row.totalCents)}</span></div>
            <PdfFrame url={companyUrl(`/purchase-invoices/${id}/pdf`)} title={row.filename} height="calc(100vh - 12rem)" />
          </Card>
        </div>
      </div>
    </>
  );
}
